import { Box, Button, Stack, Typography } from '@mui/material'
import { LocationCard } from 'Components/Cards/LocationCard/LocationCard'
import { useEffect, useState } from 'react'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { type location, type ChildAuditAccessTypeEnum, type postError } from 'core/types'
import { NotAllowed } from 'Routes/ProtectedRoute'
import { toast } from 'react-toastify'
import { LocationDetails } from 'Components/Cards/LocationCard/LocationDetails'
import { Modal } from 'Components/Modal'
import { MapStateName } from 'Components/Cards/LocationCard/mapStateName'
import { usePermissions } from 'hooks/use-permissions'

interface MultiCardListProps {
  childID: number
  requestingModule?: ChildAuditAccessTypeEnum
  readOnly?: boolean
  onDataChange?: () => void
}

export function LocationCardList (props: MultiCardListProps): JSX.Element {
  const [locationList, setLocationList] = useState<Array<{ id: number, locationId: number }>>([])
  const [primaryLocationID, setPrimaryLocationID] = useState<number>(0)
  const [refreshTime, setRefreshTime] = useState(new Date())
  const [unauthorized, setUnauthorized] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [newlocation, setNewLocation] = useState<location>({ address: '', apartment: '', city: '', id: 0, note: '', state: '', zipCode: '' })
  const perms = usePermissions()
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response, success, error, status } = await sendGet(`/ChildDetails/ChildAddresses?id=${props.childID}`, {})
      if (success) {
        const addrs = response as location[]
        const locations: Array<{ id: number, locationId: number }> = addrs?.map((a, i) => {
          if (a.isPrimary ?? false) {
            setPrimaryLocationID(a.id)
          }
          return { id: i, locationId: a.id }
        })
        setLocationList(locations)
      } else {
        if (status === 401) {
          setUnauthorized(true)
        }
        error.forEach(e => toast.error(e))
      }
    }
    void loadData()
  }, [refreshTime, props.childID])

  const onDeleteClick = (id: number): void => {
    const tempArray = [...locationList]
    tempArray.splice(tempArray.findIndex(v => v.id === id), 1)
    setLocationList([...tempArray])
  }

  const addClick = async (): Promise<void> => {
    setShowAddModal(true)
  }

  const dataChanged = (): void => {
    setRefreshTime(new Date())
    if (props.onDataChange != null) {
      props.onDataChange()
    }
  }

  const handlePlaceSelected = async (place: Microsoft.Maps.ISuggestionResult): Promise<void> => {
    const newplace: location = { ...newlocation }
    newplace.address = place.address.addressLine
    newplace.city = place.address.locality
    newplace.apartment = ''
    newplace.state = MapStateName(place.address.adminDistrict)
    newplace.zipCode = place.address.postalCode
    // newplace.isPrimary = false
    setNewLocation(newplace)
  }

  const handlePlaceChange = (e: any): void => {
    setNewLocation({ ...newlocation, [e.target.name]: e.target.value })
  }

  const addLocation = async (): Promise<void> => {
    const submitData: { entityId: number, Location: location, isPrimary: boolean } = { entityId: props.childID, Location: newlocation, isPrimary: false }
    const { response: rsp } = await sendPost('/CareNetworkEdit/CreateLocation', submitData)
    const result: { newRecordID: number, errors: postError[], success: boolean } = rsp
    if (result.success) {
      setRefreshTime(new Date())
      setShowAddModal(false)
      dataChanged()
    } else {
      result.errors?.forEach((e) => {
        toast.error(e.error)
      })
    }
  }

  if (unauthorized) return <NotAllowed />

  return <Box className='CardDisplay'>
    <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Addresses</Typography>
    <Stack direction='column' spacing={1}>
      {locationList?.sort(l => (l.id === primaryLocationID ? 0 : 1)).map((val) => {
        return <LocationCard
          key={val.locationId}
          locationId={val.locationId}
          childId={props.childID}
          isPrimary={val.locationId === primaryLocationID}
          deleteDisabled={false}
          onDelete={() => { onDeleteClick(val.id) }}
          setRefreshTime={() => { dataChanged() }}
          readOnly={!perms.Enrollment_Edit && !perms.CareNetwork_Enrollment_Edit}
        />
      })}
      {(perms.Enrollment_Edit || perms.CareNetwork_Enrollment_Edit) && (

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button color='primary' variant='contained' onClick={addClick} data-testid='addLocation' sx={{ textTransform: 'none', px: 4 }}>Add New Address</Button>
        </Box>
      )}
    </Stack>

    <Modal onClose={() => { setShowAddModal(false) }}
      titleContent={<div className='modalTitle'>Add Location</div>}
      onConfirm={addLocation}
      confirmButtonText='Save Address'
      open={showAddModal}
      maxWidth='sm'
      confirmationContent={<LocationDetails
        editData={newlocation}
        childID={props.childID}
        onChange={handlePlaceChange}
        onPlaceSelected={handlePlaceSelected}
        hidePrimary={true}
        showHomelessOption={true}
      />}
    />
  </Box>
}
